import { apiClient, handleApiRequest } from './client'
import type { BasicCascadePreviewResponse } from '@/types/teacher-request'
import type { SubjectRebindPreview, SubjectRebindResult, SubjectRebindUndo } from './timetable'

export interface TimetableEntryResponse {
  id: string
  date: string | null
  subject_name: string | null
  room_name: string | null
  klass_name: string | null
  lesson_number: number | null
  is_recurring: boolean
  is_active: boolean
}

export interface TeacherBooking {
  id: string
  request_id?: string
  date: string | null
  start_time: string | null
  end_time: string | null
  subject_name: string
  klass_name: string
  room_name: string
  requester_name: string
  role: string
  accepted_at: string | null
  confirmed_at: string | null
  payment_type?: 'school' | 'private' | null
  director_enabled?: boolean
  substitute_payout?: 'paid_out' | 'lesson_account' | null
  can_change_finance?: boolean
  hr_module_enabled?: boolean
  finance_school_default?: 'paid_out' | 'lesson_account' | null
  finance_payout?: 'paid_out' | 'lesson_account' | null
}

export interface UpcomingBookingsResponse {
  bookings: TeacherBooking[]
}

export interface TimetableWeekResponse {
  week_start: string
  week_end: string
  entries: TimetableEntryResponse[]
}

export interface TimeSlotResponse {
  id: string
  name: string
  start_time: string
  end_time: string
  slot_number?: number | null
  is_break?: boolean
}

export interface ColleagueTeacher {
  id: string
  last_name: string
  first_name: string
  teacher_code: string | null
}

export interface TeacherTimetableEntry {
  id: string
  teacher_code: string | null
  day_of_week: string | null
  time_slot_label: string | null
  lesson_number: number | null
  subject: string | null
  subject_id?: string | null
  subject_token?: string | null
  subject_unclear?: boolean
  room: string | null
  room_id?: string | null
  class_name: string | null
  klass_id?: string | null
  is_multi_period: boolean
  lesson_count: number
  multi_period_position: 'first' | 'middle' | 'last' | null
  raw_content: string
  is_active: boolean
  is_manual_entry: boolean
  is_cancelled: boolean
  cancelled_reason: string | null
  date: string | null
  week_offset: number
  version: number
  start_time?: string | null
  end_time?: string | null
  is_clipped?: boolean
  substitution_excluded?: boolean
}

export interface TeacherTimetableEntryUpdate {
  subject?: string
  room?: string
  class_name?: string
  time_slot_label?: string
}

export interface TeacherTimetableEntryCreate {
  day_of_week?: string
  time_slot_label: string
  subject?: string
  room?: string
  class_name?: string
  lesson_number?: number
  date?: string
}

export interface SubstitutionRequestResponse {
  id: string
  requesting_teacher_name: string
  status: string
  date: string
  subject_name: string | null
  room_name: string | null
  klass_name: string | null
  reason: string | null
  substitute_teacher_name: string | null
  created_at: string
  accepted_at: string | null
}

export interface SubstitutionRequestCreate {
  timetable_id: string
  date: string
  lesson_count: number
  reason?: string
  notes?: string
  selected_dates?: string[]
}

export interface RespondToRequest {
  response: 'accept' | 'decline' | 'interested'
  message?: string
}

export interface LessonResponseItem {
  lesson_request_id: string
  response: 'accept' | 'decline' | 'interested'
  decline_reason?: string
  decline_message?: string
}

export interface LessonBulkRespondResult {
  success: boolean
  total_responses: number
  acceptances: number
  declines: number
  results: Array<{
    success: boolean
    lesson_request_id: string
    response?: string
    error_code?: string | null
    message?: string | null
    skipped?: boolean
    block_id?: string
  }>
  request_status: string
  teacher_name?: string
  error_code?: string
  message?: string
}

export interface SubstitutionCancelRequest {
  reason: string
}

export interface PrepSummary {
  total_lessons: number
  lessons_with_prep: number
  has_prep: boolean
  prep_titles: string[]
  has_open_prep_request?: boolean
  open_prep_request_reason?: string | null
}

export interface CascadeStep {
  rank: number
  status: string
  is_me: boolean
}

export interface IncomingRequest {
  id: string
  status: string
  cascade_status?: string | null
  cascade_steps?: CascadeStep[]
  viewer_rank?: number | null
  viewer_step_status?: string | null
  viewer_is_last_remaining?: boolean
  viewer_answer_deadline?: string | null
  next_answer_deadline?: string | null
  is_direct_assignment?: boolean
  direct_lesson_ids?: string[]
  request_flow?: string
  request_mode?: string
  max_visible_cascade_badges?: number
  cascade_preview_enabled?: boolean
  date: string | null
  date_to?: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  time_slot_name: string | null
  lesson_count: number
  requesting_teacher_id?: string
  substitute_teacher_id?: string
  accepted_teacher_code?: string | null
  requesting_teacher_name: string
  requesting_teacher_short_code: string
  created_on_behalf?: boolean
  created_by_name?: string | null
  on_behalf_headline?: string | null
  created_at: string | null
  accepted_at: string | null
  reason: string | null
  response_message?: string | null
  is_in_panic_zone: boolean
  is_nothilfe?: boolean
  is_multi_day?: boolean
  is_urgent?: boolean
  prep_summary?: PrepSummary
  already_covered?: boolean
  lessons_accepted?: number
  lessons_declined?: number
  lessons_pending?: number
  is_partially_filled?: boolean
  cancellation_pending?: boolean
  // Wartepool-Felder (optional, bis Wartepool-System implementiert)
  my_commitment_level?: 'definitive' | 'provisional' | null
  waiting_pool_deadline?: string | null
  waiting_pool_pending_count?: number
  waiting_pool_total_counted?: number
  waiting_pool_progress?: number
  finance_eligible?: boolean
  hr_module_enabled?: boolean
  finance_school_default?: 'lesson_account' | 'paid_out'
  finance_payout?: 'lesson_account' | 'paid_out' | null
  finance_source?: 'self' | 'default' | 'hr' | 'settled' | null
  finance_binding_at?: string | null
  finance_can_edit?: boolean
  finance_lock_reason?:
    | 'decider_hr'
    | 'not_in_play'
    | 'settled'
    | 'hr_override'
    | 'binding'
    | 'payer_not_private'
    | null
  hr_enabled?: boolean
}

export interface IncomingRequestsResponse {
  requests: IncomingRequest[]
  total: number
  pending_count: number
}

export interface RequestLesson {
  id: string
  coverage_block_id?: string | null
  date: string | null
  time_slot_name: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  status: string
  assigned_teacher_id?: string | null
  assigned_teacher_name?: string | null
  assigned_teacher_short_code?: string | null
  my_response?: 'accepted' | 'interested' | 'declined' | 'pending' | null
  can_respond?: boolean
  can_un_decline?: boolean
  block_type?: 'full_day' | 'partial' | null
  confirmed_at?: string | null
  assignment_state?: 'confirmed' | 'accepted' | 'waiting' | 'accepted_not_selected' | 'declined' | 'pending' | string
}

export interface RequestLessonsResponse {
  request_id: string
  request_status: string
  lessons: RequestLesson[]
}

export interface RadarBubbleLesson {
  lesson_request_id?: string
  date: string | null
  time_slot_name: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  status: string | null
  // assigned-fix-v1: wer hat diese Lektion tatsächlich übernommen
  assigned_teacher_short_code?: string | null
  assigned_teacher_name?: string | null
}

export interface CascadeTeacherEntry {
  code: string
  rank: number
  teacher_id?: string
}

export interface RadarBubble {
  id: string
  request_id: string
  lesson_request_id: string
  distance_from_center: number
  bubble_radius: number
  bubble_color: string
  status: string
  bubble_type?: string
  hours_until_lesson: number
  is_in_panic_zone: boolean
  is_in_middle_zone: boolean
  is_in_outer_zone: boolean
  // Angular position on radar (degrees, -90 = top/North)
  angle_degrees: number
  // Cluster info
  cluster_id: string | null
  cluster_index: number
  lesson_date: string | null
  time_slot_name: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  lesson_count: number
  requester_teacher_id: string
  requester_short_code: string
  requester_name: string
  requester_faculty: string | null
  assigned_teacher_id: string | null
  assigned_teacher_short_code: string | null
  assigned_teacher_name: string | null
  teachers_contacted: number
  teachers_accepted: number
  teachers_declined: number
  teachers_pending: number
  // Prep status (Phase 4)
  prep_open?: boolean
  prep_requested?: boolean
  prep_status_summary?: string | null
  // Storno pending (storno-fix): open storno confirmation on this request -> breathe
  cancellation_pending?: boolean
  // Teacher must act on a director follow-up (returned case and/or documents)
  director_action_pending?: boolean
  // Unread chat message on this request for the viewing teacher
  has_unread?: boolean
  // Konzept A: Lektionen ohne Vertretungslehrer -> manuelle Admin-Zuweisung nötig
  needs_admin_assignment?: boolean
  // frost-v2 (Frost-Überzug V4): Anfrage hängt am Direktor-Gate (director_pending)
  // und die Lektionsrolle ist offen (pending/interested). Incoming immer false.
  gate_pending?: boolean
  // ISO-Timestamp aus director_gate_reason.evaluated_at (seit wann eingefroren), null ohne Grund-JSON
  gate_since?: string | null
  // Multi-Request Support (Phase bubbles-v2)
  request_ids?: string[]
  lesson_request_ids?: string[]
  // Bubble Splitting (Phase bubble-v2)
  is_split?: boolean
  split_role?: 'filled' | 'declined' | 'pending' | 'open' | 'cancelled' | 'expired' | 'interested' | 'assigned_elsewhere'
  // Partial Fulfillment (Phase bubbles-v2)
  original_lesson_count?: number
  effective_lesson_count?: number
  // Cascade-Lehrer pro Split-Rolle (yellow-fix-v2): wer hat uebernommen / abgelehnt /
  // ist noch kontaktiert. Nur auf gespaltenen Bubbles belegt.
  cascade_teachers?: {
    accepted?: CascadeTeacherEntry[]
    declined?: CascadeTeacherEntry[]
    candidates?: CascadeTeacherEntry[]
    overflow_count?: number
    // rk-fix-v2 (Root Cause 2): per-Lektion Interesse/Absage aus SubstitutionResponse
    // assigned (assigned-fix-v1): Lehrer mit tatsächlicher Zuweisung (grünes Badge)
    per_lesson?: Record<string, { interested: string[]; declined: string[]; assigned?: string[] }>
  }
  // Structured lesson list for tooltip display (Phase anzeige-v1)
  lessons?: RadarBubbleLesson[]
}

export interface RadarRay {
  id: string
  label: string
  angle_degrees: number
  type: 'date' | 'teacher'
  bubble_count: number
}

export interface TeacherPosition {
  id: string
  short_code: string
  name: string
  faculty: string | null
  angle_degrees: number
  has_active_requests: boolean
  active_request_count: number
}

export interface ZoneConfig {
  threshold_hours: number | null
  type: string
  color: string
  opacity: number
  label: string
}

export interface RadarZones {
  panic_zone: ZoneConfig
  middle_zone: ZoneConfig
  outer_zone: ZoneConfig
}

export interface RadarResponse {
  school_id: string
  generated_at: string
  time_range: { days: number; from: string; to: string }
  zones: RadarZones
  bubbles: RadarBubble[]
  rays: RadarRay[]
  teacher_positions?: TeacherPosition[]
  radar_type: 'teacher' | 'admin'
}

// ---------------------------------------------------------------------------
// Radar Filter types (filter-fix-v2). Canonical definitions; UI helpers live
// in components/radar/types/filter.ts.
// ---------------------------------------------------------------------------
export type RadarStatusBucket =
  | 'pending' | 'interested' | 'filled' | 'declined'
  | 'cancelled' | 'expired' | 'assigned_elsewhere'

export type RadarZone = 'panic' | 'middle' | 'outer'
export type RadarBubbleType = 'outgoing' | 'incoming'
export type RadarType = 'teacher' | 'admin'

export interface RadarFilterSpecials {
  prep_open: boolean
  prep_requested: boolean
  storno_pending: boolean
  director_action_pending: boolean
  has_unread: boolean
  is_split: boolean
  partial: boolean
  needs_admin_assignment: boolean
  // frost-v2: Filter-Special «Beim Direktor» (Frost-Bubbles)
  director_gate_pending: boolean
}

export interface RadarFilterState {
  status: RadarStatusBucket[]
  zones: RadarZone[]
  types: RadarBubbleType[]
  subjects: string[]
  faculties: string[]
  classes: string[]
  requesters: string[]
  specials: RadarFilterSpecials
  activeOnly: boolean
}

export interface UserRadarFilterResponse {
  radar_type: RadarType
  time_range_days: number | null
  filter_state: RadarFilterState
  allow_override: boolean
  is_locked: boolean
  default_state: RadarFilterState
}

export interface UserRadarFilterPayload {
  radar_type: RadarType
  time_range_days?: number | null
  filter_state?: RadarFilterState
}

export interface RadarConfigResponse {
  school_id: string
  default_time_range_days: number
  min_time_range_days: number
  max_time_range_days: number
  min_bubble_size: number
  max_bubble_size: number
  bubble_size_scale: string
  teacher_positioning: string
  show_teacher_labels: boolean
  show_radius_vectors: boolean
  max_bubbles_rendered: number
  show_incoming_in_radar: boolean
  enable_priority_entry_radar: boolean
  enable_bubble_aggregation: boolean
  enable_bubble_splitting: boolean
  color_accepted?: string
  color_interested?: string
  color_pending?: string
  color_declined?: string
  color_cancelled?: string
  show_interested_distinctly?: boolean
  zone_config?: RadarZones
  // bubblenew-v2: Admin-Radar Feature-Flags (bubble_size_scale existiert oben)
  admin_date_fan_enabled?: boolean
  admin_badge_ring_enabled?: boolean
  admin_fan_overlap_fallback?: boolean
  // Radar filter config (filter-fix-v2)
  radar_filter_allow_teacher_override?: boolean
  radar_filter_default_state?: RadarFilterState | null
  updated_at: string | null
}

export interface SendRadarMessageRequest {
  recipient_teacher_ids: string[]
  message_text: string
  include_lesson_details: boolean
}

export interface SendRadarMessageResult {
  success: boolean
  sent_count: number
  message: string
}

export const teacherApi = {
  // Timetable (old endpoint - still used by overview tab)
  getMyTimetable: (weekStart?: string) => {
    const q = weekStart ? `?week_start=${weekStart}` : ''
    return handleApiRequest<TimetableWeekResponse>(apiClient.get(`/api/v1/teacher/timetable${q}`))
  },
  refreshTimetable: () => handleApiRequest(apiClient.post('/api/v1/teacher/timetable/refresh')),

  // Timetable entries (new endpoint for dashboard grid)
  getMyTimetableEntries: (weekStart?: string, includeCancelled?: boolean, teacherId?: string) => {
    const qs = new URLSearchParams()
    if (weekStart) qs.set('week_start', weekStart)
    if (includeCancelled !== undefined) qs.set('include_cancelled', String(includeCancelled))
    if (teacherId) qs.set('teacher_id', teacherId)
    const q = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<TeacherTimetableEntry[]>(apiClient.get(`/api/v1/teacher/timetable/entries${q}`))
  },
  listColleagues: (q?: string) =>
    handleApiRequest<{ self_id: string; teachers: ColleagueTeacher[] }>(
      apiClient.get('/api/v1/teacher/colleagues', { params: q ? { q } : undefined }),
    ),
  getMyTimeSlots: () =>
    handleApiRequest<TimeSlotResponse[]>(apiClient.get('/api/v1/teacher/time-slots')),
  getTimetableGrid: () =>
    handleApiRequest<{ days: string[]; rows: Array<{ label: string; lesson?: number | null }> }>(
      apiClient.get('/api/v1/teacher/timetable/grid'),
    ),
  updateTimetableEntry: (id: string, data: TeacherTimetableEntryUpdate, version?: number) => {
    const headers: Record<string, string> = {}
    if (version !== undefined) {
      headers['If-Match'] = String(version)
    }
    return handleApiRequest<TeacherTimetableEntry>(apiClient.patch(`/api/v1/teacher/timetable/entries/${id}`, data, { headers }))
  },
  createTimetableEntry: (data: TeacherTimetableEntryCreate) =>
    handleApiRequest<TeacherTimetableEntry>(apiClient.post('/api/v1/teacher/timetable/entries', data)),
  deleteTimetableEntry: (id: string) =>
    handleApiRequest<{ message: string }>(apiClient.delete(`/api/v1/teacher/timetable/entries/${id}`)),
  previewSubjectRebind: (entryId: string) =>
    handleApiRequest<SubjectRebindPreview>(apiClient.get(`/api/v1/teacher/timetable/entries/${entryId}/subject-rebind`)),
  rebindSubject: (entryId: string, data: { subject_id?: string; target_id?: string; source_category?: string; target_category?: string; include_siblings?: boolean }) =>
    handleApiRequest<SubjectRebindResult>(apiClient.post(`/api/v1/teacher/timetable/entries/${entryId}/subject-rebind`, data)),
  undoSubjectRebind: (data: SubjectRebindUndo) =>
    handleApiRequest<{ restored_ids: string[]; alias_removed: boolean }>(
      apiClient.post('/api/v1/teacher/timetable/subject-rebind/undo', data),
    ),

  // My substitution requests
  getMySubstitutions: (params?: { status_filter?: string; date_from?: string; date_to?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status_filter) qs.set('status_filter', params.status_filter)
    if (params?.date_from) qs.set('date_from', params.date_from)
    if (params?.date_to) qs.set('date_to', params.date_to)
    const q = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<SubstitutionRequestResponse[]>(apiClient.get(`/api/v1/teacher/substitutions${q}`))
  },
  createSubstitution: (data: SubstitutionRequestCreate) =>
    handleApiRequest<SubstitutionRequestResponse>(apiClient.post('/api/v1/teacher/substitutions', data)),
  deleteSubstitution: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/teacher/substitutions/${id}`)),
  requestCancellation: (id: string, data: SubstitutionCancelRequest) =>
    handleApiRequest(apiClient.post(`/api/v1/teacher/substitutions/${id}/cancel-request`, data)),

  // Incoming requests
  getIncomingRequests: (params?: { status_filter?: string; date_from?: string; date_to?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status_filter) qs.set('status_filter', params.status_filter)
    if (params?.date_from) qs.set('date_from', params.date_from)
    if (params?.date_to) qs.set('date_to', params.date_to)
    const q = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<IncomingRequestsResponse>(apiClient.get(`/api/v1/teacher/incoming-requests${q}`))
  },
  getIncomingRequestDetail: (requestId: string) =>
    handleApiRequest<IncomingRequest>(apiClient.get(`/api/v1/teacher/incoming-requests/${requestId}`)),
  respondToRequest: (requestId: string, data: RespondToRequest) =>
    handleApiRequest(apiClient.post(`/api/v1/teacher/incoming-requests/${requestId}/respond`, data)),
  respondToLessons: (
    requestId: string,
    responses: LessonResponseItem[],
  ) =>
    handleApiRequest<LessonBulkRespondResult>(
      apiClient.post(`/api/v1/teacher/incoming-requests/${requestId}/respond-lessons`,
        { responses }),
    ),
  setSubstitutePayout: (requestId: string, value: 'paid_out' | 'lesson_account') =>
    handleApiRequest<{
      success: boolean
      finance_payout: 'paid_out' | 'lesson_account'
      finance_source: string
      finance_can_edit: boolean
      finance_lock_reason: string | null
    }>(apiClient.post(
      `/api/v1/teacher/incoming-requests/${requestId}/finance/substitute-payout`,
      { value },
    )),
  getRequestLessons: (requestId: string) =>
    handleApiRequest<RequestLessonsResponse>(apiClient.get(`/api/v1/teacher/incoming-requests/${requestId}/lessons`)),
  unDeclineLesson: (requestId: string, lessonRequestId: string) =>
    handleApiRequest<{ success: boolean; response: string; message: string }>(
      apiClient.patch(`/api/v1/teacher/incoming-requests/${requestId}/lessons/${lessonRequestId}/un-decline`)
    ),
  getIncomingCascadePreview: (requestId: string) =>
    handleApiRequest<BasicCascadePreviewResponse>(
      apiClient.get(`/api/v1/teacher/incoming-requests/${requestId}/cascade-preview`)
    ),

  // Teacher Radar
  getTeacherRadarBubbles: (timeRangeDays?: number, includeIncoming?: boolean, signal?: AbortSignal) => {
    const params = new URLSearchParams()
    if (timeRangeDays) params.set('time_range_days', String(timeRangeDays))
    if (includeIncoming) params.set('include_incoming', 'true')
    const q = params.toString() ? `?${params.toString()}` : ''
    return handleApiRequest<RadarResponse>(apiClient.get(`/api/v1/teacher-radar/bubbles${q}`, { signal }))
  },
  getRadarConfig: async () => {
    return handleApiRequest<RadarConfigResponse>(apiClient.get('/api/v1/radar/config'))
  },

  // Per-user radar filter preferences (filter-fix-v2)
  getRadarUserFilters: (radarType: RadarType, signal?: AbortSignal) =>
    handleApiRequest<UserRadarFilterResponse>(
      apiClient.get(`/api/v1/radar/user-filters`, { params: { radar_type: radarType }, signal })
    ),
  saveRadarUserFilters: (payload: UserRadarFilterPayload) =>
    handleApiRequest<UserRadarFilterResponse>(
      apiClient.put(`/api/v1/radar/user-filters`, payload)
    ),
  getLessonTeachers: (lessonRequestIds: string[], responseType: 'declined' | 'pending' | 'accepted') =>
    handleApiRequest<{ teacher_ids: string[] }>(
      apiClient.post(`/api/v1/teacher-radar/bubbles/multi/teachers?response_type=${responseType}`, {
        lesson_request_ids: lessonRequestIds,
      })
    ),
  sendRadarReminder: (lessonRequestIds: string[], customMessage?: string) =>
    handleApiRequest<SendRadarMessageResult>(
      apiClient.post('/api/v1/teacher-radar/bubbles/multi/reminder', {
        lesson_request_ids: lessonRequestIds,
        custom_message: customMessage || '',
      })
    ),
  sendRadarMessage: (lessonRequestIds: string[], data: Omit<SendRadarMessageRequest, 'lesson_request_ids'>) =>
    handleApiRequest<SendRadarMessageResult>(
      apiClient.post('/api/v1/radar/bubbles/multi/message', {
        lesson_request_ids: lessonRequestIds,
        ...data,
      })
    ),
  getRequestDetails: (requestId: string) =>
    handleApiRequest<{
      id: string
      date: string | null
      subject_name: string | null
      klass_name: string | null
      room_name: string | null
      status: string
      total_lessons: number
      lessons_filled: number
      lessons_pending: number
      lessons_declined: number
      lesson_request_ids: string[]
      prep_summary?: PrepSummary
    }>(apiClient.get(`/api/v1/radar/requests/${requestId}/details`)),
  
  // Calendar Feed
  // dashboard-v1: ohne limit nutzt das Backend die persönliche Einstellung
  // 'Vertretungen im Dashboard' (Schul-Default oder Lehrer-Override).
  getUpcomingBookings: (limit?: number) =>
    handleApiRequest<UpcomingBookingsResponse>(
      apiClient.get(`/api/v1/teacher/bookings/upcoming${limit != null ? `?limit=${limit}` : ''}`)
    ),

  // dashboard-v3/v4: effektive Dashboard-Konfiguration (Panel-Sichtbarkeit,
  // Anzeige-Limit — 0 blendet die Rubrik aus —, Kalender-Verbinden-Freigabe)
  getDashboardConfig: () =>
    handleApiRequest<{
      bookings_panel_enabled: boolean
      bookings_limit: number
      calendar_connect_enabled: boolean
    }>(
      apiClient.get('/api/v1/teacher/dashboard-config')
    ),
}

// Teacher Import API (Phase 38)
export const teacherImportApi = {
  previewImport: (uploaded_file_id: string, column_mapping?: Record<string, string>) =>
    handleApiRequest(apiClient.post('/api/v1/teachers/import/preview', { uploaded_file_id, column_mapping })),
  executeImport: (uploaded_file_id: string, column_mapping?: Record<string, string>) =>
    handleApiRequest(apiClient.post('/api/v1/teachers/import/execute', { uploaded_file_id, column_mapping })),
  listFaculties: (includeInactive = false) =>
    handleApiRequest(apiClient.get(`/api/v1/teachers/faculties?include_inactive=${includeInactive}`)),
  createFaculty: (data: { name: string; code?: string; description?: string; color?: string }) =>
    handleApiRequest(apiClient.post('/api/v1/teachers/faculties', data)),
  updateFaculty: (id: string, data: Partial<{ name: string; code: string; description: string; color: string; is_active: boolean }>) =>
    handleApiRequest(apiClient.patch(`/api/v1/teachers/faculties/${id}`, data)),
  deleteFaculty: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/teachers/faculties/${id}`)),
}

export const teacherSettingsApi = {
  getMySettings: () =>
    handleApiRequest(apiClient.get('/api/v1/substitution-requests/my-settings')),

  updateMySettings: (overrides: Record<string, unknown>) =>
    handleApiRequest(apiClient.put('/api/v1/substitution-requests/my-settings', { overrides })),

  // settingKey may be prefixed ("cascade:some_key") for non-substitution_request fields
  resetMySetting: (settingKey: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/substitution-requests/my-settings/${encodeURIComponent(settingKey)}`)),

  resetAllMySettings: () =>
    handleApiRequest(apiClient.delete('/api/v1/substitution-requests/my-settings')),
}

// V3/B7b: Finanz-Entscheidungen der Lehrer (Direktor-Konzept Kap. 5.3)
export const teacherFinanceApi = {
  setRequesterDeduction: (requestId: string, value: 'ipb' | 'salary', note?: string) =>
    handleApiRequest<{ success: boolean }>(
      apiClient.post(`/api/v1/teacher/substitutions/${requestId}/finance/requester-deduction`, { value, note })),
}
